import { Component, Input } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { nodeMetaDataJsonSchema7 } from '@sciflow/schema';
import { selectTemplate } from 'export';
import { NodeMetaEditorDialog } from 'projects/ui-components/src';
import { Node as PMNode } from 'prosemirror-model';
import { EditorView } from 'prosemirror-view';
import { firstValueFrom, map } from 'rxjs';

import { updateMarkAttrs, updateNodeAttrs } from '../commands';

interface MetadataMarkContext {
  type: string;
  attrs: Record<string, any>;
  id?: string | null;
  from?: number;
  to?: number;
}

@Component({
  selector: 'sfo-metadata-menu',
  templateUrl: './metadata-menu.component.html',
  styleUrls: ['./metadata-menu.component.scss'],
  standalone: false,
})
export class MetadataMenuComponent {
  private currentNode: PMNode | null = null;
  private currentMark: MetadataMarkContext | null = null;
  private mode: 'node' | 'mark' | null = null;
  private markDisplayName: string | null = null;

  @Input() set node(value: PMNode | null) {
    this.currentNode = value ?? null;
    if (value) {
      this.mode = 'node';
    } else if (!this.currentMark) {
      this.mode = null;
    }
  }

  @Input() set mark(value: MetadataMarkContext | null) {
    this.currentMark = value && value.type ? value : null;
    if (this.currentMark) {
      this.mode = 'mark';
      this.currentNode = null;
      this.markDisplayName = this.getMarkDisplayName(this.currentMark);
    } else if (!this.currentNode) {
      this.mode = null;
      this.markDisplayName = null;
    }
  }

  @Input() editorView?: EditorView;
  @Input() label?: string;
  @Input() tooltip?: string;

  constructor(
    private dialog: MatDialog,
    private store: Store,
  ) {}

  get buttonLabel(): string {
    if (this.label) {
      return this.label;
    }

    if (this.mode === 'mark' && this.currentMark) {
      return `Edit`;
    }

    if (this.mode === 'node' && this.currentNode) {
      const typeName = this.currentNode.type?.name;
      return typeName ? `Edit ${typeName}` : 'Edit metadata';
    }

    return 'Edit metadata';
  }

  get buttonTooltip(): string {
    if (this.tooltip) {
      return this.tooltip;
    }

    if (this.mode === 'mark' && this.currentMark) {
      const displayName =
        this.markDisplayName ?? this.formatTypeLabel(this.currentMark.type) ?? 'metadata';
      return `Edit ${displayName}`;
    }

    if (this.mode === 'node') {
      return 'Edit metadata';
    }

    return 'Edit metadata';
  }

  async openEditDialog() {
    if (this.mode === 'node' && this.currentNode) {
      await this.openNodeDialog();
      return;
    }

    if (this.mode === 'mark' && this.currentMark) {
      await this.openMarkDialog();
    }
  }

  private async openNodeDialog() {
    const node = this.currentNode;
    if (!node) {
      return;
    }

    const nodeType = node.type?.name;
    const nodeSchemas = await firstValueFrom(
      this.store.select(selectTemplate).pipe(map((template) => template?.prosemirrorSchemas?.nodes)),
    );
    const schema = (nodeType && nodeSchemas?.[nodeType]) || nodeMetaDataJsonSchema7?.[nodeType];

    const dialogRef: MatDialogRef<NodeMetaEditorDialog> = this.dialog.open(NodeMetaEditorDialog, {
      data: {
        node,
        schema: schema ?? {},
        renderMode: 'simple',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !this.editorView) {
        return;
      }
      const id = node.attrs?.id;
      if (!id) {
        return;
      }
      updateNodeAttrs(id, result)(this.editorView.state, this.editorView.dispatch);
    });
  }

  private async openMarkDialog() {
    const markContext = this.currentMark;
    if (!markContext) {
      return;
    }

    const markType = markContext.type;
    const markSchema = await firstValueFrom(
      this.store
        .select(selectTemplate)
        .pipe(map((template) => template?.prosemirrorSchemas?.marks?.[markType] || null)),
    );

    const fakeNode: any = {
      attrs: { ...markContext.attrs },
      type: { name: markType },
    };

    const dialogRef: MatDialogRef<NodeMetaEditorDialog> = this.dialog.open(NodeMetaEditorDialog, {
      data: {
        node: fakeNode, 
        schema: markSchema ?? {},
        renderMode: 'simple',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !this.editorView) {
        return;
      }

      const attrs = {
        ...markContext.attrs,
        ...result,
      };

      const id = attrs?.id ?? markContext.id ?? markContext.attrs?.id;
      if (id && attrs && !attrs.id) {
        attrs.id = id;
      }

      updateMarkAttrs(markType, attrs, {
        id,
        from: markContext.from,
        to: markContext.to,
      })(this.editorView.state, this.editorView.dispatch);

      this.currentMark = {
        type: markType,
        attrs,
        id: markContext.id ?? attrs.id ?? null,
        from: markContext.from,
        to: markContext.to,
      };
      this.markDisplayName = this.getMarkDisplayName(this.currentMark);
    });
  }

  private getMarkDisplayName(mark: MetadataMarkContext): string | null {
    const attrs = mark.attrs || {};
    const preferredKeys = ['label', 'title', 'name', 'raw'];
    for (const key of preferredKeys) {
      const value = attrs[key];
      const extracted = this.extractStringValue(value);
      if (extracted) {
        return extracted;
      }
    }

    // Fallback: find first non-empty string attribute.
    for (const value of Object.values(attrs)) {
      const extracted = this.extractStringValue(value);
      if (extracted) {
        return extracted;
      }
    }

    return this.formatTypeLabel(mark.type);
  }

  private extractStringValue(value: unknown, seen = new Set<unknown>()): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    if (value == null) {
      return null;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        const extracted = this.extractStringValue(entry, seen);
        if (extracted) {
          return extracted;
        }
      }
      return null;
    }

    if (typeof value === 'object') {
      if (seen.has(value)) {
        return null;
      }
      seen.add(value);
      for (const entry of Object.values(value as Record<string, unknown>)) {
        const extracted = this.extractStringValue(entry, seen);
        if (extracted) {
          return extracted;
        }
      }
    }

    return null;
  }

  private formatTypeLabel(type?: string | null): string | null {
    if (!type) {
      return null;
    }
    const spaced = type
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();
    if (spaced.length === 0) {
      return null;
    }
    return spaced
      .split(' ')
      .filter((segment) => segment.length > 0)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }
}
